import { type TableInformation } from '../../../config/flavourConfig';
import type {
    CatDTO,
    DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import type {
    AdultAccidentDTO,
    ChildAccidentDTO,
    YoungAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import type { TravelDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/tra';
import { toLongDateString } from '../../../utils/dateUtil';
import type {
    PrivateContentDTO,
    YouthContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import type {
    ChoDTO,
    ChoHouseDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/cho';
import type {
    PrhHolidayHomeDTO,
    PrhHouseDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import type {
    CaravanDTO,
    TrailerDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/uvp';
import { formatStringToMonetaryValueString } from '../../../utils/coverablesUtil';
import { FLAVOUR_NAME } from '../../../constants/coverable';

export const getCoverableInformationCoverableDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case FLAVOUR_NAME.DOG:
            return getDogInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.CAT:
            return getCatInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.ACC_ACCIDENT:
        case FLAVOUR_NAME.ACC_YOUNG:
            return getAdultYoungAccidentInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.ACC_CHILD:
            return getChildAccidentInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.TRA_HOUSEHOLD:
        case FLAVOUR_NAME.TRA_INDIVIDUAL:
            return getTravelInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.PRC_YOUTH_CONTENT:
            return getYouthContentInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.PRC_CONTENT:
            return getPrivateContentInformationCoverableDetailsConfig(coverable);
        case FLAVOUR_NAME.MVP_CAR:
            return getPrivateCarInformationCoverableDetailsConfig(
                coverable as CarDTO,
                getHippoText
            );
        case FLAVOUR_NAME.CHO:
            if ((coverable as ChoDTO).insuranceVariant === 'Lejlighed')
                return getCHOApartmentInformationCoverableDetailsConfig(coverable as ChoDTO);
            else
                return getCHOHouseInformationCoverableDetailsConfig(
                    coverable as ChoHouseDTO,
                    getHippoText
                );
        case FLAVOUR_NAME.PRH_HOUSE:
            return getPrhHouseInformationCoverableDetailsConfig(
                coverable as PrhHouseDTO,
                getHippoText
            );
        case FLAVOUR_NAME.PRH_HOLIDAY_HOME:
            return getPrhHolidayHouseInformationCoverableDetailsConfig(
                coverable as PrhHolidayHomeDTO,
                getHippoText
            );
        case FLAVOUR_NAME.UVP_CARAVAN:
            return getCaravanInformationCoverableDetailsConfig(
                coverable as CaravanDTO,
                getHippoText
            );
        case FLAVOUR_NAME.UVP_TRAILER:
            return getTrailerInformationCoverableDetailsConfig(
                coverable as TrailerDTO,
                getHippoText
            );
        default:
            return undefined;
    }
};

const getPrhHolidayHouseInformationCoverableDetailsConfig = (
    coverable: PrhHolidayHomeDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Oplysninger om forsikringsstedet',
        rowsInformation: [
            {
                name: 'Adresse',
                fieldValue: coverable.fullAddress
            },
            {
                name: 'Matrikelnummer',
                fieldValue: coverable.cadastralNumber
            },
            {
                name: 'Opførelsesår',
                fieldValue: coverable.constructionYear.toString()
            },
            {
                name: 'Bebygget areal',
                fieldValue: `${coverable.developedArea.toString()} m2`,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE3'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY3')
                    }
                }
            },
            {
                name: 'Etager (ekskl. kælder)',
                fieldValue: coverable.numberOfFloors
            },
            {
                name: 'Udhus- og garageareal',
                fieldValue: `${coverable.garageOuthouseArea?.toString() ?? 0} m2`
            },
            {
                name: 'Tagtype',
                fieldValue: coverable.roofType
            },
            {
                name: 'Murtype',
                fieldValue: coverable.exteriorWallType,
                hidden: !coverable.exteriorWallType
            },
            {
                name: 'Antal toiletter',
                fieldValue: coverable.numberOfToilets?.toString()
            },
            {
                name: 'Opvarmning',
                fieldValue: coverable.primaryHeatingType
            },
            {
                name: 'Brændeovn/Pejs',
                fieldValue: coverable.fireplace
            },
            {
                name: 'Nedlagt landbrug',
                fieldValue: coverable.closedDownFarm,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-TITLE'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-BODY')
                    }
                },
                hidden: !coverable.closedDownFarm
            },
            {
                name: 'Nedlagt erhverv',
                fieldValue: coverable.closedDownCommercial,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-TITLE'),
                        description: getHippoText(
                            'DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-BODY'
                        )
                    }
                },
                hidden: !coverable.closedDownCommercial
            },
            {
                name: 'Fredet eller bevaringsværdigt',
                fieldValue: coverable.listedWorthPreservingType,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-TITLE'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-BODY')
                    }
                },
                hidden: !coverable.listedWorthPreservingType
            },
            {
                name: 'Vandsikringsanlæg',
                fieldValue: coverable.waterProtectionType,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-TITLE'),
                        description: getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-BODY'
                        )
                    }
                },
                hidden: !coverable.waterProtectionType
            }
        ]
    };
};

const getPrhHouseInformationCoverableDetailsConfig = (
    coverable: PrhHouseDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Oplysninger om forsikringsstedet',
        rowsInformation: [
            {
                name: 'Adresse',
                fieldValue: coverable.fullAddress
            },
            {
                name: 'Matrikelnummer',
                fieldValue: coverable.cadastralNumber
            },
            {
                name: 'Opførelsesår',
                fieldValue: coverable.constructionYear.toString()
            },
            {
                name:
                    coverable.basementArea === 0
                        ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1')
                        : getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE2'),
                fieldValue: `${coverable.livingArea.toString()} m2`,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title:
                            coverable.basementArea === 0
                                ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1')
                                : getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE2'),
                        description:
                            coverable.basementArea === 0
                                ? getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY1')
                                : getHippoText('DSS-COVERABLE-INFORMATION- DEVELOPEDAREA-BODY2')
                    }
                }
            },
            {
                name: 'Bebygget areal',
                fieldValue: `${coverable.developedArea.toString()} m2`,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE3'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-BODY3')
                    }
                }
            },
            {
                name: 'Etager (ekskl. kælder)',
                fieldValue: coverable.numberOfFloors
            },
            {
                name: 'Kælderareal',
                fieldValue: `${coverable.basementArea?.toString() ?? 0} m2`
            },
            {
                name: 'Udhus- og garageareal',
                fieldValue: `${coverable.garageOuthouseArea?.toString() ?? 0} m2`
            },
            {
                name: 'Tagtype',
                fieldValue: coverable.roofType
            },
            {
                name: 'Murtype',
                fieldValue: coverable.exteriorWallType
            },
            {
                name: 'Antal toiletter',
                fieldValue: coverable.numberOfToilets?.toString()
            },
            {
                name: 'Opvarmning',
                fieldValue: coverable.primaryHeatingType
            },
            {
                name: 'Erhverv på ejendommen',
                fieldValue: coverable.commercialActivitiesInTheBuilding
            },
            {
                name: 'Nedlagt landbrug',
                fieldValue: coverable.closedDownFarm,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-TITLE'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-FARM-BODY')
                    }
                }
            },
            {
                name: 'Nedlagt erhverv',
                fieldValue: coverable.closedDownCommercial,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-TITLE'),
                        description: getHippoText(
                            'DSS-COVERABLE-INFORMATION-CLOSED-COMMERCIAL-BODY'
                        )
                    }
                }
            },
            {
                name: 'Fredet eller bevaringsværdigt',
                fieldValue: coverable.listedWorthPreservingType,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-TITLE'),
                        description: getHippoText('DSS-COVERABLE-INFORMATION-PRESERVED-BODY')
                    }
                }
            },
            {
                name: 'Vandsikringsanlæg',
                fieldValue: coverable.waterProtectionType,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-TITLE'),
                        description: getHippoText(
                            'DSS-COVERABLE-INFORMATION-STEP-WATERPROTECTION-BODY'
                        )
                    }
                }
            },
            {
                name: 'Døre/vinduer er ældre end 30 år og af træ',
                fieldValue: coverable.allDoorsAndWindowsOver30AndMadeOfWood,
                hidden: !coverable.allDoorsAndWindowsOver30AndMadeOfWood
            }
        ]
    };
};

const getCHOHouseInformationCoverableDetailsConfig = (
    coverable: ChoHouseDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Oplysninger om Forsikringsstedet',
        rowsInformation: [
            {
                name: 'Adresse',
                fieldValue: coverable.address
            },
            {
                name: 'Anvendelse',
                fieldValue: coverable.insuranceVariant
            },
            {
                name: 'Matrikelnummer',
                fieldValue: coverable.cadastralNumber
            },
            {
                name: 'Opførelsesår',
                fieldValue: coverable.constructionYear.toString()
            },
            {
                name: 'Bebygget areal',
                fieldValue: coverable.developedArea.toString(),
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: getHippoText('DSS-INFO-MODAL-CHOLOB-DEVELOPEDAREA-TITLE'),
                        description: getHippoText('DSS-INFO-MODAL-CHOLOB-DEVELOPEDAREA-BODY')
                    }
                }
            },
            {
                name: 'Antal etager (ekskl. kælder)',
                fieldValue: coverable.floorsNumber
            },
            {
                name: 'Kælder',
                fieldValue: coverable.basement
            },
            {
                name: 'Antal toiletter',
                fieldValue: coverable.toiletsNumber.toString()
            }
        ]
    };
};

const getCHOApartmentInformationCoverableDetailsConfig = (coverable: ChoDTO): TableInformation => {
    return {
        title: 'Oplysninger om Forsikringsstedet',
        rowsInformation: [
            {
                name: 'Adresse',
                fieldValue: coverable.address
            },
            {
                name: 'Anvendelse',
                fieldValue: coverable.insuranceVariant
            },
            {
                name: 'Opførelsesår',
                fieldValue: coverable.constructionYear.toString()
            }
        ]
    };
};

const getDogInformationCoverableDetailsConfig = (dogCoverable: DogDTO): TableInformation => {
    return {
        title: 'Oplysninger om ' + (dogCoverable.name ?? 'forsikring'),
        rowsInformation: [
            {
                name: 'Race',
                fieldValue: dogCoverable.breed
            },
            {
                name: 'Hundens anvendelse',
                fieldValue: dogCoverable.dogUse
            },
            {
                name: 'Køn',
                fieldValue: dogCoverable.gender
            },
            {
                name: 'Født',
                fieldValue: dogCoverable.dateOfBirth
            },
            {
                name: 'Chip-nummer / ID-nummer',
                fieldValue: dogCoverable.chipIdNumber ?? 'Ikke oplyst'
            },
            {
                name: 'Tjenestehund-ID-nummer',
                fieldValue: dogCoverable.policeDogID ?? 'Ikke oplyst',
                hidden: dogCoverable.dogUse !== 'Tjenestehund'
            },
            {
                name: 'Medlemsskab af hundeklub',
                fieldValue: dogCoverable.dogClub
            }
        ]
    };
};

const getCatInformationCoverableDetailsConfig = (catCoverable: CatDTO): TableInformation => {
    return {
        title: 'Oplysninger om ' + (catCoverable.name ?? 'forsikring'),
        rowsInformation: [
            {
                name: 'Race',
                fieldValue: catCoverable.breed
            },
            {
                name: 'Køn',
                fieldValue: catCoverable.gender
            },
            {
                name: 'Født',
                fieldValue: catCoverable.dateOfBirth
            },
            {
                name: 'Chip-nummer / ID-nummer',
                fieldValue: catCoverable.chipIdNumber ?? 'Ikke oplyst'
            },
            {
                name: 'Udekat',
                fieldValue: catCoverable.outdoorCat ? 'Ja' : 'Nej'
            }
        ]
    };
};

const getAdultYoungAccidentInformationCoverableDetailsConfig = (
    accidentCoverable: AdultAccidentDTO | YoungAccidentDTO
): TableInformation => {
    return {
        title: 'Oplysninger om dig',
        rowsInformation: [
            {
                name: 'Fulde navn',
                fieldValue: accidentCoverable.fullName
            },
            {
                name: 'CPR-nummer',
                fieldValue: accidentCoverable.cprNumber
            }
        ]
    };
};

const getChildAccidentInformationCoverableDetailsConfig = (
    childCoverable: ChildAccidentDTO
): TableInformation => {
    return {
        title: childCoverable.name ? `Oplysninger om ${childCoverable.name}` : '',
        rowsInformation: [
            {
                name: 'Fulde navn',
                fieldValue: childCoverable.fullName,
                hidden: !childCoverable.fullName
            },
            {
                name: 'CPR-nummer',
                fieldValue: childCoverable.cprNumber,
                hidden: !childCoverable.cprNumber
            },
            {
                name: 'Født',
                fieldValue: toLongDateString(childCoverable.dateOfBirth),
                hidden: !!childCoverable.cprNumber || !childCoverable.dateOfBirth
            }
        ]
    };
};

const getTravelInformationCoverableDetailsConfig = (
    travelCoverable: TravelDTO
): TableInformation => {
    const isHouseholdFlavour = travelCoverable.flavourName === 'Rejseforsikring';
    const coverableName = isHouseholdFlavour
        ? travelCoverable.name
        : `${travelCoverable.insuredFullName ?? ''} - og evt. delebørn`;
    const modalTitleKey = isHouseholdFlavour
        ? 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-HOUSEHOLD-HEADER'
        : 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-INDIVIDUAL-HEADER';
    const modalDescriptionKey = isHouseholdFlavour
        ? 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-HOUSEHOLD-DESCRIPTION'
        : 'DSS-COVERABLE-DETAILS-MODAL-TRAVEL-INDIVIDUAL-DESCRIPTION';

    return {
        title: 'Hvem og hvad er omfattet?',
        rowsInformation: [
            {
                name: 'Personer, som er dækket',
                fieldValue: coverableName,
                valueType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: modalTitleKey,
                        description: modalDescriptionKey
                    }
                }
            },
            {
                name: 'Dækningsperiode',
                fieldValue: 'Alle årets ferier i op til 60 dage i træk'
            }
        ]
    };
};

const getYouthContentInformationCoverableDetailsConfig = (
    youthCoverable: YouthContentDTO
): TableInformation => {
    return {
        title: 'Oplysninger om forsikringssted',
        rowsInformation: [
            {
                name: 'Forsikringssted',
                fieldValue: youthCoverable.insuredAddress
            }
        ]
    };
};

const getPrivateContentInformationCoverableDetailsConfig = (
    contentCoverable: PrivateContentDTO
): TableInformation => {
    return {
        title: 'Oplysninger om forsikringssted',
        rowsInformation: [
            {
                name: 'Forsikringssted',
                fieldValue: contentCoverable.insuredAddress
            },
            {
                name: 'Antal beboere tilmeldt din folkeregisteradresse',
                fieldValue: contentCoverable.numberInHousehold?.toString(),
                hidden: contentCoverable.hasCohousing
            },
            {
                name: 'Antal beboere med folkeregisteradresse i bofællesskabet',
                fieldValue: contentCoverable.numberInHousehold?.toString(),
                hidden: !contentCoverable.hasCohousing,
                labelType: 'modalLink',
                modalInformation: {
                    modalType: ViewHippoInformationModal,
                    modalProps: {
                        title: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-TITLE',
                        description:
                            'DSS-CON-HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-DESCRIPTION'
                    }
                }
            },
            {
                name: 'Medforsikret ægtefælle/samlever med anden folkeregisteradresse',
                fieldValue: contentCoverable.coinsuredName,
                hidden: !contentCoverable.coinsuredBirthday && !contentCoverable.coinsuredName
            },
            {
                name: 'Medforsikret ægtefælle/samlever er født',
                fieldValue: contentCoverable.coinsuredBirthday,
                hidden: !contentCoverable.coinsuredBirthday && !contentCoverable.coinsuredName
            },
            {
                name: 'Tagtype',
                fieldValue: contentCoverable.roofType
            }
        ]
    };
};

const getPrivateCarInformationCoverableDetailsConfig = (
    carCoverable: CarDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: getHippoText('DSS-COVERABLE-DETAILS-INFORMATION-ON-THE-CAR-HEADER'),
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-REGISTRATION-PLATE-TITLE'),
                fieldValue: carCoverable.registrationNumber
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FIRST- REGISTRATION-DATE-TITLE'),
                fieldValue: carCoverable.firstRegistrationDate
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAKE-AND-MODEL-TITLE'),
                fieldValue: `${carCoverable.make} ${carCoverable.model}`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VARIANT-TITLE'),
                fieldValue: carCoverable.variant
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-YEAR-TITLE'),
                fieldValue: carCoverable.year.toString()
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-ENGINE-SIZE-TITLE'),
                fieldValue: carCoverable.enginePower
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FUEL-TYPE-TITLE'),
                fieldValue: carCoverable.fuelType
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAXIMUM-POWER-TITLE'),
                fieldValue: `${carCoverable.power.toString()} kW`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VIN-NUMBER-TITLE'),
                fieldValue: carCoverable.vin
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-THE-CAR-IS-LEASED-TITLE'),
                fieldValue: carCoverable.isInLeasing
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-LEASING-PERIOD-TITLE'),
                fieldValue: `${toLongDateString(carCoverable.leasingStartDate)}
                - ${toLongDateString(carCoverable.leasingEndDate)}`,
                hidden: !carCoverable.isInLeasing_bool
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-THE-CAR-IS-USED-FOR-TITLE'),
                fieldValue: carCoverable.vehicleUse
            }
        ]
    };
};

const getCaravanInformationCoverableDetailsConfig = (
    caravanCoverable: CaravanDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Oplysninger om Campingvognen',
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-REGISTRATION-PLATE-TITLE'),
                fieldValue: caravanCoverable.regNumber
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FIRST- REGISTRATION-DATE-TITLE'),
                fieldValue: caravanCoverable.firstRegDate ?? ''
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAKE-AND-MODEL-TITLE'),
                fieldValue: `${String(caravanCoverable.make)} ${String(caravanCoverable.model)}`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-YEAR-TITLE'),
                fieldValue: caravanCoverable.modelYear?.toString()
            },
            {
                name: 'Aktuelle værdi',
                fieldValue: formatStringToMonetaryValueString(
                    caravanCoverable.currentValue?.toString() ?? ''
                )
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VIN-NUMBER-TITLE'),
                fieldValue: caravanCoverable.vin
            }
        ]
    };
};

const getTrailerInformationCoverableDetailsConfig = (
    trailerCoverable: TrailerDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Oplysninger om påhængsvognen',
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-REGISTRATION-PLATE-TITLE'),
                fieldValue: trailerCoverable.regNumber
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-FIRST- REGISTRATION-DATE-TITLE'),
                fieldValue: trailerCoverable.firstRegDate ?? ''
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-MAKE-AND-MODEL-TITLE'),
                fieldValue: `${String(trailerCoverable.make)} ${String(trailerCoverable.model)}`
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-YEAR-TITLE'),
                fieldValue: trailerCoverable.modelYear?.toString()
            },
            {
                name: 'Aktuelle værdi',
                fieldValue: formatStringToMonetaryValueString(
                    trailerCoverable.currentValue?.toString() ?? ''
                )
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-VIN-NUMBER-TITLE'),
                fieldValue: trailerCoverable.vin
            }
        ]
    };
};
